import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ValueType, OptionsType } from 'react-select';
import { useToasts } from 'react-toast-notifications';

import Loader from '../../loader';
import Select from '../select';

import MapController from '../../../../controllers/MapController';
import HansenChartController from '../../../../controllers/HansenChartController';

import { RootState } from '../../../../reducers/app';
import { toggleMapLoader } from '../../../../reducers/mapview/actions';

import { IPerformAuditCriteriaData } from '../../../../types/auditdata';
import { ICriterion } from '../../../../types/audit-config';
import { IOptionsType } from '../../../../types/react-select-options';

import './criterion-section.scss';

/**
 * Component enabling a user to audit a specific criterion
 */
const CriterionSection = ({
  criterion,
  performAuditLayer,
}: {
  criterion: ICriterion;
  performAuditLayer: IPerformAuditCriteriaData | undefined;
}) => {
  // LOCAL STATE
  const [selectedGeom, setSelectedGeom] = useState<IOptionsType>();

  // REDUX
  const myLayers = useSelector((state: RootState) => state.mapview.myLayers);
  const auditArea = useSelector((state: RootState) => state.auditdata.auditArea.graphic);
  const loaderActive = useSelector((state: RootState) => state.leftpanel.loader);
  const dispatch = useDispatch();

  useEffect(() => {
    if (criterion.relevantLayers.length > 0) {
      setSelectedGeom(criterion.relevantLayers[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (performAuditLayer) {
      setSelectedGeom({ value: performAuditLayer.selectedLayer.id, label: performAuditLayer.selectedLayer.title });
        HansenChartController.createHansenChart(criterion.criteria, performAuditLayer.hansenCounts);
    } // this is in place to repopulate the dropdowns if the user navigates away
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [performAuditLayer]);

  const { addToast } = useToasts();

  const isCriterionLayer = (id: string) => {
    switch (id) {
      case 'fsc-certified-forests':
      case 'hydrology':
      case 'archeological-sites-brazil':
      case 'tree-cover-loss-by-dominant-driver':
      case 'sentinel':
      case 'landsat':
      case 'terrain':
      case 'hansen':
        return false;
      default:
        return true;
    }
  };

  const createLayerOptions = () => {
    // const options: Array<IOptionsType> = []; // KEEPING IN CASE THEY CHANGE THEIR MIND BACK TO OLD WAY
    const options: Array<IOptionsType> = MapController.mapLayers
      .filter((layer) => isCriterionLayer(layer.id))
      .map((layer) => {
        return { value: layer.id, label: layer.title };
      });

    myLayers.forEach((layer) => {
      if (!layer.id.includes('raster-analysis')) {
        // filter out raster analysis layers like NDVI
        options.push({ value: layer.id, label: layer.title });
      }
    });

    return options.sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1));
  };

  // this function checks if our selected option matches the type signature for a React Select value
  const isOptionsType = (layers: ValueType<IOptionsType>): layers is OptionsType<IOptionsType> => {
    return layers ? true : false;
  };

  const disabled = () => {
    if (!auditArea) {
      return true;
    }
    return isOptionsType(selectedGeom) ? false : true;
  };

  const calculateCriterionAnalysis = () => {
    MapController.applyPerformAuditMask(criterion.criteria, selectedGeom!.value)
      .then((res: string) => {
        addToast(res, { appearance: 'success', autoDismiss: true });
        dispatch(toggleMapLoader(false));
      })
      .catch((res: string) => {
        addToast(res, { appearance: 'error', autoDismiss: true });
        dispatch(toggleMapLoader(false));
      });
  };

  const removeCriterionAnalysis = (criteriaId: string) => {
    MapController.removePerformAuditLayer(criteriaId);
  };

  // KEEPING IN CASE THEY CHANGE THEIR MIND BACK TO OLD WAY
  // const updateCheckbox = () => {
  //   let value: any = criterion.relevantLayers[0]; // typed as any because we need to reassign value to '' to get the select to clear it's value and not look blank

  //   if (selectedGeom === criterion.relevantLayers[0]) {
  //     value = '';
  //   }

  //   setSelectedGeom(value);
  // };

  return (
    <div className='section'>
      {loaderActive && <Loader />}
      {/* {criterion.relevantLayers.length > 0 && (
        <div className='select-checkbox'>
          <div className='title'>Relevant Layer</div>
          <div className='checkbox'>
            <input
              type='checkbox'
              id={criterion.relevantLayers[0].value}
              name={criterion.relevantLayers[0].label}
              checked={selectedGeom?.value === criterion.relevantLayers[0].value}
              onChange={() => updateCheckbox()}
            />
            <div className='title'>{criterion.relevantLayers[0].label}</div>
          </div>
        </div> // KEEPING IN CASE THEY CHANGE THEIR MIND BACK TO OLD WAY
      )} */}
      <div className='select-geom'>
        <div className='title'>Select Geometry</div>
        <Select
          isMulti={false}
          options={createLayerOptions()}
          value={selectedGeom}
          onChange={setSelectedGeom}
          // isDisabled={selectedGeom === (criterion.relevantLayers.length > 0 && criterion.relevantLayers[0])} // KEEPING IN CASE THEY CHANGE THEIR MIND BACK TO OLD WAY
          isDisabled={false}
          placeholder='Select from My Layers'
        />
      </div>
      {performAuditLayer && (
        <div
          id={`criterion-${criterion.criteria}-area`}
          key={`criterion-${criterion.criteria}-area`}
          className='total-area'
        >
          <div className='title'>Primary Area of Interest</div>
          <div className='subtitle'>Total Area (ha)</div>
          <div className='area'>
            {performAuditLayer?.area.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
      )}
      {performAuditLayer && <div id={`criterion-${criterion.criteria}-chart`} key={`criterion-${criterion.criteria}-chart`} />}
      <div className='section__button'>
        {performAuditLayer ? (
          <div className='reset' onClick={() => removeCriterionAnalysis(criterion.criteria)}>
            Reset Criterion
          </div>
        ) : (
          <button className='green-button' disabled={disabled()} onClick={() => calculateCriterionAnalysis()}>
            Calculate
          </button>
        )}
      </div>
    </div>
  );
};

export default CriterionSection;
